// Shared Employee form used for Add + Edit
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"];
const BRANCHES = ["Bengaluru HQ", "Mumbai Branch", "Hyderabad Branch", "Pune Branch", "Delhi NCR", "Remote India"];
const STATUSES = ["Active", "On Leave", "Notice Period", "Exited"];

const empty = {
  employee_code: "", name: "", email: "", phone: "",
  designation: "", department: "Engineering", branch: "Bengaluru HQ",
  reporting_manager: "", joining_date: "", status: "Active",
  blood_group: "Unknown", skills: "", aadhaar: "", pan: "",
  bank_name: "", bank_account: "",
  emergency_contact_name: "", emergency_contact_phone: "",
};

export default function EmployeeForm({ mode, employee, departments, employees, onDone }) {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode === "edit" && employee) {
      setF({
        ...empty,
        ...employee,
        skills: (employee.skills || []).join(", "),
        reporting_manager: employee.reporting_manager || "",
      });
    } else {
      setF(empty);
    }
  }, [mode, employee]);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        skills: (typeof f.skills === "string" ? f.skills : "").split(",").map(s => s.trim()).filter(Boolean),
      };
      if (mode === "edit") {
        // strip immutable/system fields
        const { id, employee_code, email, avatar_url, ...upd } = payload;
        await api.put(`/employees/${employee.id}`, upd);
        toast.success(`${f.name} updated`);
      } else {
        await api.post("/employees", payload);
        toast.success(`${f.name} added`);
      }
      onDone && onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const isEdit = mode === "edit";

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Employee code *"><Input required disabled={isEdit} value={f.employee_code} onChange={e => set("employee_code", e.target.value)} placeholder="TT-013" data-testid="ef-code" /></Field>
      <Field label="Full name *"><Input required value={f.name} onChange={e => set("name", e.target.value)} data-testid="ef-name" /></Field>
      <Field label="Work email *"><Input type="email" required disabled={isEdit} value={f.email} onChange={e => set("email", e.target.value)} data-testid="ef-email" /></Field>
      <Field label="Phone"><Input value={f.phone} onChange={e => set("phone", e.target.value)} data-testid="ef-phone" /></Field>
      <Field label="Designation *"><Input required value={f.designation} onChange={e => set("designation", e.target.value)} data-testid="ef-designation" placeholder="Senior Cloud Engineer" /></Field>
      <Field label="Department *">
        <Select value={f.department} onValueChange={v => set("department", v)}>
          <SelectTrigger data-testid="ef-department"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(departments || []).filter(d => d && d.name && d.name.trim()).map(d => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Branch">
        <Select value={f.branch} onValueChange={v => set("branch", v)}>
          <SelectTrigger data-testid="ef-branch"><SelectValue /></SelectTrigger>
          <SelectContent>{BRANCHES.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Reporting manager">
        <Select value={f.reporting_manager || "__none__"} onValueChange={v => set("reporting_manager", v === "__none__" ? "" : v)}>
          <SelectTrigger data-testid="ef-manager"><SelectValue placeholder="None (top level)" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None (reports to CEO)</SelectItem>
            {(employees || []).filter(e => e && e.name && e.name.trim()).map(e => <SelectItem key={e.id} value={e.name}>{e.name} · {e.designation}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Joining date *"><Input type="date" required value={f.joining_date} onChange={e => set("joining_date", e.target.value)} data-testid="ef-joining" /></Field>
      <Field label="Status">
        <Select value={f.status} onValueChange={v => set("status", v)}>
          <SelectTrigger data-testid="ef-status"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Blood group">
        <Select value={f.blood_group} onValueChange={v => set("blood_group", v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{BLOOD_GROUPS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Aadhaar"><Input value={f.aadhaar} onChange={e => set("aadhaar", e.target.value)} /></Field>
      <Field label="PAN"><Input value={f.pan} onChange={e => set("pan", e.target.value)} /></Field>
      <Field label="Bank name"><Input value={f.bank_name} onChange={e => set("bank_name", e.target.value)} /></Field>
      <Field label="Bank account"><Input value={f.bank_account} onChange={e => set("bank_account", e.target.value)} /></Field>
      <Field label="Emergency contact name"><Input value={f.emergency_contact_name} onChange={e => set("emergency_contact_name", e.target.value)} /></Field>
      <Field label="Emergency contact phone"><Input value={f.emergency_contact_phone} onChange={e => set("emergency_contact_phone", e.target.value)} /></Field>
      <div className="md:col-span-2">
        <Field label="Skills (comma separated)">
          <Textarea rows={2} value={f.skills} onChange={e => set("skills", e.target.value)} placeholder="VMware, AWS, Terraform" />
        </Field>
      </div>
      <div className="md:col-span-2 flex justify-end">
        <Button disabled={busy} type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="ef-submit">
          {isEdit ? "Save changes" : "Add employee"}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
